import type { Brand } from "../../../shared/types";
import { withQueryParams } from "./path-query";

export const BRAND_SETTINGS_SECTION_KEYS = [
  "profile",
  "strategy",
  "voice",
  "context-docs",
  "surfaces",
  "corrections",
] as const;

export type BrandSettingsSectionKey = (typeof BRAND_SETTINGS_SECTION_KEYS)[number];

export const LEGACY_BRAND_TAB_TO_ROUTE = {
  profile: "settings/profile",
  strategy: "artifacts/strategy",
  voice: "artifacts/voice",
  voice_ai: "docs",
  connections: "settings/connections",
  corrections: "settings/corrections",
  corpus: "/corpus",
  skills: "",
  monitor: "",
  research: "/corpus",
} as const;

export type LegacyBrandTabKey = keyof typeof LEGACY_BRAND_TAB_TO_ROUTE;

export const BRAND_ARTIFACT_TYPES = ["voice", "strategy"] as const;
export type BrandArtifactType = (typeof BRAND_ARTIFACT_TYPES)[number];
export const BRAND_ARTIFACT_META: Record<
  BrandArtifactType,
  {
    settingsSection: Extract<BrandSettingsSectionKey, BrandArtifactType>;
    tabLabel: string;
    workspaceTitle: string;
  }
> = {
  voice: {
    settingsSection: "voice",
    tabLabel: "Voice",
    workspaceTitle: "Voice artifact",
  },
  strategy: {
    settingsSection: "strategy",
    tabLabel: "Strategy",
    workspaceTitle: "Strategy artifact",
  },
};
export const BRAND_ARTIFACT_TYPE_PATTERN = BRAND_ARTIFACT_TYPES.join("|");
export const BRAND_ARTIFACT_ROUTE_PATTERN = new RegExp(
  `^/brand/([^/]+)/artifacts/(${BRAND_ARTIFACT_TYPE_PATTERN})/?$`,
);

const BRAND_ROUTE_PATTERN = /^\/(?:brand|agent)\/([^/?#]+)(?:\/|$)/;
// Single source of truth for the operator-route alternation. Both patterns
// below derive from it so a new operator route can never be added to one
// without the other — preventing the exact "/home silently ignores ?brandId="
// desync this fix addresses from recurring on a future route.
const GLOBAL_OPERATOR_ROUTE_ALTERNATION = "inbox|review|tasks|workflows|runs|outputs|settings|usage|billing|account(?:/[^/?#]+)?";
const GLOBAL_OPERATOR_ROUTE_PATTERN = new RegExp(
  `^/(?:${GLOBAL_OPERATOR_ROUTE_ALTERNATION})(?:/|$)`,
);
// Routes that read `?brandId=` from search for their active-brand context:
// the global-operator routes PLUS `/home`. `/home` is deliberately NOT in
// `GLOBAL_OPERATOR_ROUTE_PATTERN` (see `isGlobalOperatorPath`) so it keeps its
// `list[0]` landing fallback when no `?brandId=` is present.
const QUERY_BRAND_ROUTE_PATTERN = new RegExp(
  `^/(?:home|${GLOBAL_OPERATOR_ROUTE_ALTERNATION})(?:/|$)`,
);

function decodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function parseBrandIdFromPath(pathname: string): string | null {
  const match = BRAND_ROUTE_PATTERN.exec(pathname);
  return match?.[1] ? decodePathSegment(match[1]) : null;
}

export function isGlobalOperatorPath(pathname: string): boolean {
  return GLOBAL_OPERATOR_ROUTE_PATTERN.test(pathname);
}

/**
 * Routes that resolve their active brand from `?brandId=` in search.
 * Superset of the global-operator routes plus `/home`, which honors the query
 * param for selection continuity while keeping its `list[0]` landing fallback.
 */
export function isQueryBrandRoute(pathname: string): boolean {
  return QUERY_BRAND_ROUTE_PATTERN.test(pathname);
}

export function parseBrandIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get("brandId");
  return value ? decodePathSegment(value) : null;
}

export function normalizeBrandId(
  value: string | null | undefined,
  brands: ReadonlyArray<Pick<Brand, "id">> = [],
): string | null {
  if (!value) return null;
  const exact = brands.find((brand) => brand.id === value);
  if (exact) return exact.id;

  const matches = brands.filter((brand) => brand.id.startsWith(value));
  return matches.length === 1 ? matches[0]!.id : value;
}

export function resolveBrandContextId(pathname: string, search: string): string | null {
  const routeBrandId = parseBrandIdFromPath(pathname);
  if (routeBrandId) return routeBrandId;
  if (!isQueryBrandRoute(pathname)) return null;
  return parseBrandIdFromSearch(search);
}

/**
 * Sub-paths under `/brand/:id` that the Brand hub rail item owns.
 *
 * This is an ALLOWLIST on purpose. It used to be `startsWith("/brand/:id/")` minus a
 * growing list of exceptions, which meant every new brand-scoped section lit the Brand
 * rail item too until someone remembered to add another `!isXActive` term — analytics is
 * exactly the section that was missed (PRD-0096 §A.4 E5). With an allowlist a new section
 * is dark by default rather than wrongly active by default.
 */
export const BRAND_HUB_SEGMENTS = [
  "connections",
  "context-docs",
  "corpus",
  "corrections",
  "monitor",
  "profile",
  "research",
  "settings",
  "skills",
  "strategy",
  "surfaces",
  "visuals",
  "voice",
  "voice-discovery",
  "voice_ai",
] as const;

/**
 * Brand-scoped segments a DIFFERENT rail item owns. Listed explicitly so the split is
 * auditable: every `/brand/:id/*` segment in App.tsx belongs to exactly one of these two
 * lists, and a route in neither leaves the rail with no active item at all — the failure
 * mode an allowlist introduces if it is under-populated.
 */
/**
 * Segments the DESKTOP rail assigns to a dedicated item, but which the mobile bottom nav
 * has no tab for — so Brand remains their owner on mobile. Without this, inverting
 * isBrandHubPath() to an allowlist left these routes with no active mobile item at all.
 *
 * `campaigns` is deliberately absent: it had no mobile owner before this change either
 * (isBrandHubPath returned false for it explicitly), so giving it one is a product
 * decision, not a regression fix.
 */
export const MOBILE_BRAND_OWNED_SEGMENTS = ["analytics", "artifacts", "data-views"] as const;

export const NON_HUB_BRAND_SEGMENTS = [
  "actions",    // Actions rail item
  "analytics",  // Analytics rail item
  "artifacts",  // Docs rail item (isDocsActive)
  "assets",     // Assets rail item
  "campaigns",  // no rail item by design — was explicitly excluded before this change too
  "data-views", // Analytics rail item — shell-routes.ts registers it with section "analytics"
  "docs",       // Docs rail item
  "publishing", // Publishing studio within Workspace
  "youtube",    // Video inventory/reports within Workspace
  "review",     // Workflows section
] as const;

export function isBrandHubPath(pathname: string, brandId?: string | null): boolean {
  if (!brandId) {
    return pathname === "/brand";
  }

  const brandBasePath = `/brand/${brandId}`;

  if (pathname === brandBasePath) {
    return true;
  }

  const rest = pathname.startsWith(`${brandBasePath}/`)
    ? pathname.slice(brandBasePath.length + 1)
    : null;
  if (rest === null) return false;

  const segment = rest.split("/")[0] ?? "";
  return (BRAND_HUB_SEGMENTS as readonly string[]).includes(segment);
}

export function buildCorpusPath(brandId?: string | null): string {
  if (!brandId) {
    return "/corpus";
  }

  return `/corpus?brandId=${encodeURIComponent(brandId)}`;
}

export function buildBrandReviewPath(brandId: string, search = ""): string {
  const params = new URLSearchParams(search);
  params.delete("brandId");
  const query = params.toString();
  const basePath = `/brand/${encodeURIComponent(brandId)}/review`;
  return query ? `${basePath}?${query}` : basePath;
}

export function buildAccountConnectionsPath(brandId?: string | null): string {
  return buildBrandScopedPath("/settings/connections", brandId);
}

export function buildBrandSettingsPath(
  brandId: string,
  section: BrandSettingsSectionKey = "profile",
): string {
  return `/brand/${encodeURIComponent(brandId)}/settings/${section}`;
}

export function buildBrandArtifactPath(brandId: string, artifactType: BrandArtifactType): string {
  return `/brand/${encodeURIComponent(brandId)}/artifacts/${artifactType}`;
}

export function isBrandArtifactType(value: string): value is BrandArtifactType {
  return BRAND_ARTIFACT_TYPES.includes(value as BrandArtifactType);
}

export function parseBrandArtifactPath(pathname: string): {
  brandId: string;
  artifactType: BrandArtifactType;
} | null {
  const match = pathname.match(BRAND_ARTIFACT_ROUTE_PATTERN);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  return {
    brandId: decodePathSegment(match[1]),
    // BRAND_ARTIFACT_ROUTE_PATTERN constrains group 2 to voice|strategy.
    artifactType: match[2] as BrandArtifactType,
  };
}

export function buildBrandScopedPath(path: string, brandId?: string | null): string {
  if (!brandId) return path;

  const parsed = new URL(path, "http://localhost");
  if (parsed.pathname === "/review") {
    return buildBrandReviewPath(brandId, parsed.search);
  }
  return withQueryParams(path, { brandId });
}

export function buildSwitchedBrandPath(pathname: string, targetBrandId: string): string | null {
  const targetId = encodeURIComponent(targetBrandId);

  // A run id belongs to the current brand. Carrying it into another brand's
  // context leaves the old result on screen and makes the switch look inert.
  // Return to the selected brand's run collection instead.
  if (/^\/runs\/[^/]+(?:\/trace)?\/?$/.test(pathname)) {
    return buildBrandScopedPath("/runs", targetBrandId);
  }

  if (/^\/brand\/[^/]+\/settings\/connections\/?$/.test(pathname)) {
    return buildAccountConnectionsPath(targetBrandId);
  }

  const settingsMatch = pathname.match(/^\/brand\/([^/]+)\/settings(?:\/(profile|strategy|voice|context-docs|connections|surfaces|corrections))?\/?$/);
  if (settingsMatch) {
    const section = settingsMatch[2];
    return section ? `/brand/${targetId}/settings/${section}` : `/brand/${targetId}/settings/profile`;
  }

  const artifactRoute = parseBrandArtifactPath(pathname);
  if (artifactRoute) {
    return `/brand/${targetId}/artifacts/${artifactRoute.artifactType}`;
  }

  if (/^\/brand\/[^/]+\/?$/.test(pathname)) {
    return `/brand/${targetId}`;
  }

  if (/^\/brand\/[^/]+\/review\/?$/.test(pathname)) {
    return `/brand/${targetId}/review`;
  }

  if (/^\/brand\/[^/]+\/campaigns\/?$/.test(pathname)) {
    return `/brand/${targetId}/campaigns`;
  }

  // Saved Action details belong to one brand. Switch to the new brand's Action list.
  if (/^\/brand\/[^/]+\/actions(?:\/.*)?$/.test(pathname)) {
    return `/brand/${targetId}/actions`;
  }

  if (/^\/agent\/[^/]+\/?$/.test(pathname)) {
    return `/agent/${targetId}`;
  }

  // Brand Docs is a first-class workspace surface (own tab + label), so switching
  // brands there must land on the new brand's docs — not eject to the brand hub
  // (the catch-all below would otherwise drop the `/docs` subpath).
  if (/^\/brand\/[^/]+\/docs\/?$/.test(pathname)) {
    return `/brand/${targetId}/docs`;
  }

  // Assets is a first-class rail section too — switching brands lands on the new
  // brand's asset library root (deeper asset ids belong to one brand, so don't
  // carry them across) instead of ejecting to the brand hub.
  if (/^\/brand\/[^/]+\/assets(?:\/.*)?$/.test(pathname)) {
    return `/brand/${targetId}/assets`;
  }

  if (/^\/brand\/[^/]+(?:\/.*)?$/.test(pathname)) {
    return `/brand/${targetId}`;
  }

  return null;
}
